import { safeIceCandidate, extractIPFromCandidate, isPrivateIP } from './utils'
import type { ICEServer } from './protocol'

export interface SFUNegotiatorEvents {
  remoteStream: (stream: MediaStream) => void
  iceCandidate: (callId: string, candidate: string, sdpMid: string, sdpMLineIndex: number) => void
  offer: (callId: string, sdp: string) => void
  answer: (callId: string, sdp: string) => void
  join: (callId: string) => void
  leave: (callId: string) => void
  stateChange: (state: RTCPeerConnectionState) => void
  error: (error: Error) => void
}

type EventHandler = (...args: unknown[]) => void

export class SFUNegotiator {
  private pc: RTCPeerConnection | null = null
  private _localStream: MediaStream | null = null
  private _ownsLocalStream = false
  private _remoteStream: MediaStream | null = null
  private pendingICE: Array<{ candidate: string; sdpMid: string; sdpMLineIndex: number }> = []
  private disconnectedTimer: ReturnType<typeof setTimeout> | null = null
  private currentCallId: string | null = null

  private listeners = new Map<string, Set<EventHandler>>()

  /**
   * 加入 SFU 会话（作为 Offerer）
   * @param ownsStream localStream 是否由 SFUNegotiator 管理生命周期（leave 时 stop）；
   *                   外部通过 setLocalStream 传入的流应传 false，由调用方管理
   */
  async join(callId: string, iceServers: ICEServer[], localStream: MediaStream, ownsStream = false): Promise<void> {
    // 如果 PC 已存在且未关闭，跳过创建
    if (this.pc && this.pc.connectionState !== 'closed') {
      console.warn('[SDK] SFU PC already exists and not closed, skipping join')
      return
    }

    // 如果 PC 已关闭，清理后重新创建
    if (this.pc) {
      console.log('[SDK] Cleaning up closed SFU PC before joining')
      this.leave()
    }

    this.currentCallId = callId
    this._localStream = localStream

    const config = {
      iceServers: iceServers.map((s) => ({
        urls: s.urls,
        username: s.username,
        credential: s.credential,
      })),
    }

    this.pc = new RTCPeerConnection(config)

    // ICE candidate → 发送给 SFU
    // 过滤内网 IP 的 host candidate，保留公网 IP 的 host candidate
    // SFU 使用 NAT1To1IPs 后发送的是公网 IP 的 host 类型候选
    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        const c = event.candidate
        const cand = c.candidate || ''
        if (cand.includes('typ host')) {
          const ip = extractIPFromCandidate(cand)
          if (isPrivateIP(ip)) {
            console.log('[SDK] filtered private-IP host candidate:', ip)
            return
          }
        }
        this.emit('iceCandidate', callId, cand, c.sdpMid || '', c.sdpMLineIndex ?? 0)
      }
    }

    // 收到远端 track
    this.pc.ontrack = (event) => {
      if (!this._remoteStream) {
        this._remoteStream = new MediaStream()
      }
      const existingTracks = this._remoteStream.getTracks()
      if (!existingTracks.some((t) => t.id === event.track.id)) {
        this._remoteStream.addTrack(event.track)
      }
      // 强制触发引用变化
      this._remoteStream = new MediaStream(this._remoteStream.getTracks())
      this.emit('remoteStream', this._remoteStream)
    }

    // 连接状态变化
    this.pc.onconnectionstatechange = () => {
      if (!this.pc) return
      const state = this.pc.connectionState
      this.emit('stateChange', state)

      if (state === 'disconnected') {
        // 瞬态：5s 后确认仍断开才触发挂断
        this.disconnectedTimer = setTimeout(() => {
          if (this.pc && this.pc.connectionState === 'disconnected') {
            this.emit('error', new Error('SFU connection disconnected'))
          }
        }, 5000)
      } else if (state === 'connected' || state === 'failed' || state === 'closed') {
        if (this.disconnectedTimer) {
          clearTimeout(this.disconnectedTimer)
          this.disconnectedTimer = null
        }
      }

      if (state === 'failed') {
        this.emit('error', new Error('SFU connection failed'))
      }
    }

    // 添加本地 track
    localStream.getTracks().forEach((track) => {
      this.pc!.addTrack(track, localStream)
    })

    // 接警员通常只发送音频（无摄像头），但需要接收设备的视频。
    // 额外添加一个 recvonly 的 video transceiver，让 offer 直接包含 m=video 行，
    // SFU 的 answer 就能直接把设备的视频轨包含在内，省掉一轮 renegotiation 往返。
    const hasVideoSender = localStream.getTracks().some((t) => t.kind === 'video')
    if (!hasVideoSender) {
      this.pc!.addTransceiver('video', { direction: 'recvonly' })
      console.log('[SDK] addTransceiver: video (recvonly)')
    }

    this.emit('join', callId)

    // 作为 Offerer 发送 offer
    const offer = await this.pc.createOffer()
    await this.pc.setLocalDescription(offer)
    this.emit('offer', callId, offer.sdp)
  }

  /**
   * 重新发送 offer（glare 恢复后使用）
   * 当 SFU 返回 glare 错误时，浏览器等待 SFU 的 renegotiation offer 处理完成后，
   * 可以调用此方法重新发送 offer
   */
  async recreateOffer(callId: string): Promise<void> {
    if (!this.pc) {
      console.error('[SDK] recreateOffer: no SFU PC')
      return
    }
    const offer = await this.pc.createOffer()
    await this.pc.setLocalDescription(offer)
    this.emit('offer', callId, offer.sdp)
  }

  /**
   * 处理 SFU renegotiation offer（Answerer 路径）
   */
  async handleOffer(callId: string, offerSDP: string): Promise<void> {
    if (!this.pc) {
      console.error('[SDK] handleOffer: no SFU PC')
      return
    }

    await this.pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: offerSDP }))
    this.flushPendingICE()

    const answer = await this.pc.createAnswer()
    await this.pc.setLocalDescription(answer)
    this.emit('answer', callId, answer.sdp)
  }

  /**
   * 处理 SFU answer（setRemoteDescription + flush pending ICE）
   */
  async handleAnswer(answerSDP: string): Promise<void> {
    if (!this.pc) {
      console.error('[SDK] handleAnswer: no SFU PC')
      return
    }

    try {
      await this.pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp: answerSDP }))
      this.flushPendingICE()
    } catch (e) {
      this.emit('error', e instanceof Error ? e : new Error(String(e)))
    }
  }

  /**
   * 添加 ICE candidate（含缓存逻辑）
   */
  async addICECandidate(candidate: string, sdpMid: string, sdpMLineIndex: number): Promise<void> {
    if (!this.pc || !this.pc.remoteDescription || !this.pc.remoteDescription.type) {
      this.pendingICE.push({ candidate, sdpMid, sdpMLineIndex })
      return
    }

    try {
      await this.pc.addIceCandidate(safeIceCandidate(candidate, sdpMid, sdpMLineIndex))
    } catch (e) {
      console.warn('[SDK] addSFUICECandidate failed:', e)
    }
  }

  /**
   * 离开 SFU 会话，释放资源
   */
  leave(): void {
    if (this.disconnectedTimer) {
      clearTimeout(this.disconnectedTimer)
      this.disconnectedTimer = null
    }

    if (this.pc) {
      this.pc.onicecandidate = null
      this.pc.ontrack = null
      this.pc.onconnectionstatechange = null
      this.pc.close()
      this.pc = null
    }

    // 仅停止由本类获取的媒体流；外部传入的流由调用方管理生命周期
    if (this._localStream && this._ownsLocalStream) {
      this._localStream.getTracks().forEach((t) => t.stop())
    }
    this._localStream = null
    this._ownsLocalStream = false

    this._remoteStream = null
    this.pendingICE = []
    this.currentCallId = null
  }

  getLocalStream(): MediaStream | null {
    return this._localStream
  }

  getRemoteStream(): MediaStream | null {
    return this._remoteStream
  }

  muteAudio(muted: boolean): void {
    if (this._localStream) {
      this._localStream.getAudioTracks().forEach((t) => (t.enabled = !muted))
    }
  }

  muteVideo(muted: boolean): void {
    if (this._localStream) {
      this._localStream.getVideoTracks().forEach((t) => (t.enabled = !muted))
    }
  }

  get hasPC(): boolean {
    return this.pc !== null
  }

  /** 当前 SFU 会话信息（重连恢复时判断是否需要重建 PC） */
  get sessionInfo(): { callId: string | null; pcAlive: boolean } {
    return {
      callId: this.currentCallId,
      pcAlive: !!this.pc && this.pc.connectionState !== 'closed',
    }
  }

  private flushPendingICE(): void {
    const candidates = this.pendingICE.splice(0)
    for (const ice of candidates) {
      this.addICECandidate(ice.candidate, ice.sdpMid, ice.sdpMLineIndex).catch(() => {})
    }
  }

  // ========== 事件 ==========

  on(event: string, handler: EventHandler): this {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(handler)
    return this
  }

  off(event: string, handler: EventHandler): this {
    this.listeners.get(event)?.delete(handler)
    return this
  }

  private emit(event: string, ...args: unknown[]): void {
    const handlers = this.listeners.get(event)
    if (handlers) {
      for (const handler of handlers) {
        try {
          handler(...args)
        } catch (e) {
          console.error(`[SDK] Error in SFU ${event} handler:`, e)
        }
      }
    }
  }
}

// ========== 媒体获取辅助 ==========

export interface LocalStreamResult {
  stream: MediaStream | null
  /** true 表示已从音视频降级为纯音频，调用方必须显式处理（禁止静默降级） */
  degraded: boolean
  /** 失败或降级原因 */
  reason?: string
}

export async function getLocalStream(
  constraints?: MediaStreamConstraints
): Promise<LocalStreamResult> {
  const c = constraints || { audio: true, video: true }

  try {
    const stream = await navigator.mediaDevices.getUserMedia(c)
    return { stream, degraded: false }
  } catch (e) {
    const errText = e instanceof Error ? e.message : String(e)
    // 降级：仅音频（显式返回 degraded，由调用方通知用户，避免"无画面"难定位）
    if (c.video) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        return { stream, degraded: true, reason: `视频获取失败，已降级为纯音频: ${errText}` }
      } catch (e2) {
        const errText2 = e2 instanceof Error ? e2.message : String(e2)
        return { stream: null, degraded: false, reason: `媒体获取失败: ${errText2}` }
      }
    }
    return { stream: null, degraded: false, reason: `媒体获取失败: ${errText}` }
  }
}
